import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { BusyButton } from "../../components/etams/BusyButton";
import { ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { catatLogAset, uploadNotaLog, listJadwalAset, getAset, apiErrorMessage } from "../../lib/asetApi";
import { label } from "../../hooks/useAsetMeta";
import { cariKaryawanRegion, ambilKaryawan, hariIniRegion, formatKm } from "./bantuAset";
import PemilihCari from "../../components/etams/PemilihCari";
import { labelKaryawan } from "../../lib/sumberPilihan";

// Dialog Catat Servis (L8, 4b-2) — dipakai dari layar Jadwal Maintenance
// (jadwal terisi, jenis bawaan PREVENTIVE/"Rutin") dan dari tab Riwayat Servis
// Detail Aset (jadwal opsional, jenis bawaan CORRECTIVE/"Perbaikan").
//
// Cermin `aset_service.catat_log` + RPC `aset_catat_log_maintenance`:
//   * tampil langsung: tanggal pelaksanaan (bawaan `hariIni` = tanggal_hari_ini_region
//     dari baris view/backend, BUKAN jam browser), jenis (meta jenis_log),
//     dikerjakan oleh (karyawan region aset ATAU teknisi luar — satu dari dua),
//     hasil (meta hasil_log), kondisi setelah (meta kondisi), biaya, km (kendaraan),
//     nota (opsional; diunggah SESUDAH log tersimpan lewat POST /log/{id}/nota).
//   * terlipat "Rincian lain": keluhan, catatan, pelapor, tanggal lapor.
//   * CHECK DB: CORRECTIVE wajib pelapor; PREVENTIVE wajib jadwal; SELESAI wajib
//     tanggal pelaksanaan; tanggal_pelaksanaan >= tanggal_lapor; masa depan ditolak
//     RPC. Layar hanya menuntun; galat backend tampil verbatim.
//   * Pelapor diprefill PIC aset (dibaca sekali lewat GET /{id} bila pemanggil
//     nol membawanya): CHECK DB "CORRECTIVE wajib pelapor" dan
//     PEMBACA_KM_WAJIB (km butuh pelaksana ATAU pelapor employee) — backend
//     nol memberi bawaan untuk KC/ADMIN (hanya untuk PIC ber-akun).
//   * tanggal_lapor kosong → dikirim = tanggal pelaksanaan: RPC mengisi
//     hari ini bila NULL, dan CHECK `tanggal_pelaksanaan >= tanggal_lapor`
//     akan menolak servis yang tanggal pelaksanaannya dimundurkan.
//   * R9: log PERMANEN — nol tombol ubah/hapus di mana pun.
//   * Nota gagal SESUDAH log tersimpan → log tetap sah; toast memberi tahu dan
//     nota bisa diunggah dari tab Riwayat Servis (sekali tulis, R9).

const TANPA = "__tanpa__";
const KOSONG = {
  tanggal_pelaksanaan: "", jenis: "", pelaksana_mode: "karyawan", dilaksanakan_oleh: "", vendor_teknisi: "",
  hasil: "", kondisi_setelah: "", biaya_idr: "", km: "", jadwal_id: "",
  keluhan: "", catatan: "", dilaporkan_oleh: "", tanggal_lapor: "",
};

/**
 * @param aset  {id, kode, nama, kategori, region_id}
 * @param jadwalAktif  daftar jadwal aktif aset (opsional; kalau null dimuat dari GET /{id}/jadwal)
 * @param jadwalId  jadwal terpilih awal (dari layar Jadwal Maintenance)
 * @param jenisBawaan  "PREVENTIVE" | "CORRECTIVE"
 * @param hariIni  YYYY-MM-DD zona region dari view/backend; null → dihitung dari zona store region
 */
export default function CatatServisDialog({ terbuka, onTutup, onSelesai, meta, aset, jadwalAktif, jadwalId, jenisBawaan, hariIni }) {
  const [form, setForm] = useState(KOSONG);
  const [rinci, setRinci] = useState(false);
  const [file, setFile] = useState(null);
  const [menyimpan, setMenyimpan] = useState(false);
  const [jadwalDaftar, setJadwalDaftar] = useState([]);
  const fileRef = useRef(null);
  const cariKaryawanAset = cariKaryawanRegion(aset?.region_id);
  const kendaraan = aset?.kategori === "KENDARAAN_BERMOTOR";
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!terbuka || !aset) return undefined;
    let hidup = true;
    setRinci(false); setFile(null);
    setForm({ ...KOSONG, jenis: jenisBawaan || "CORRECTIVE", jadwal_id: jadwalId || "", tanggal_pelaksanaan: hariIni || "",
              dilaporkan_oleh: aset.pic_employee_id || "" });
    if (!hariIni) hariIniRegion(aset.region_id).then((h) => { if (hidup && h) upd("tanggal_pelaksanaan", h); });
    if (!aset.pic_employee_id) {
      getAset(aset.id).then((r) => { if (hidup && r.data?.aset?.pic_employee_id) upd("dilaporkan_oleh", r.data.aset.pic_employee_id); }).catch((e) => toast.error(apiErrorMessage(e, "Data pendukung gagal dimuat.")));
    }
    if (Array.isArray(jadwalAktif)) {
      setJadwalDaftar(jadwalAktif.filter((j) => j.is_active !== false));
    } else {
      listJadwalAset(aset.id).then((r) => { if (hidup) setJadwalDaftar((r.data?.items || []).filter((j) => j.is_active)); }).catch((e) => toast.error(apiErrorMessage(e, "Data pendukung gagal dimuat.")));
    }
    return () => { hidup = false; };
  }, [terbuka, aset, jadwalAktif, jadwalId, jenisBawaan, hariIni]);

  const angka = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const teks = (v) => (typeof v === "string" && v.trim() === "" ? null : v.trim());

  const simpan = async () => {
    if (!form.jenis) { toast.error("Jenis servis wajib dipilih."); return; }
    if (form.jenis === "PREVENTIVE" && !form.jadwal_id) { toast.error("Servis rutin harus dikaitkan ke jadwal."); return; }
    if (!form.hasil) { toast.error("Hasil wajib dipilih."); return; }
    if (form.hasil === "SELESAI" && !form.tanggal_pelaksanaan) { toast.error("Tanggal pelaksanaan wajib diisi untuk hasil Selesai."); return; }
    if (form.pelaksana_mode === "karyawan" && !form.dilaksanakan_oleh && !form.vendor_teknisi.trim()) {
      toast.error("Pilih karyawan yang mengerjakan, atau isi teknisi luar."); return;
    }
    if (form.pelaksana_mode === "luar" && !form.vendor_teknisi.trim()) { toast.error("Nama teknisi luar wajib diisi."); return; }
    if (form.jenis === "CORRECTIVE" && !form.dilaporkan_oleh) { setRinci(true); toast.error("Servis perbaikan wajib punya pelapor — isi di Rincian lain."); return; }
    if (form.tanggal_lapor && form.tanggal_pelaksanaan && form.tanggal_lapor > form.tanggal_pelaksanaan) {
      setRinci(true); toast.error("Tanggal lapor tidak boleh sesudah tanggal pelaksanaan."); return;
    }
    setMenyimpan(true);
    try {
      const body = {
        jenis: form.jenis,
        jadwal_id: form.jadwal_id || null,
        tanggal_pelaksanaan: form.tanggal_pelaksanaan || null,
        tanggal_lapor: form.tanggal_lapor || form.tanggal_pelaksanaan || null,
        dilaksanakan_oleh: form.pelaksana_mode === "karyawan" ? (form.dilaksanakan_oleh || null) : null,
        vendor_teknisi: form.pelaksana_mode === "luar" ? teks(form.vendor_teknisi) : null,
        dilaporkan_oleh: form.dilaporkan_oleh || null,
        keluhan: teks(form.keluhan), catatan: teks(form.catatan),
        hasil: form.hasil, kondisi_setelah: form.kondisi_setelah || null,
        biaya_idr: angka(form.biaya_idr), km: kendaraan ? angka(form.km) : null,
      };
      Object.keys(body).forEach((k) => { if (body[k] === null) delete body[k]; });
      const r = await catatLogAset(aset.id, body);
      const logId = r.data?.log_id;
      let pesan = `Servis ${aset.kode} tercatat.`;
      if (r.data?.status_aset_baru) pesan += ` Status aset: ${label(meta, "status_aset", r.data.status_aset_baru)}.`;
      if (file && logId) {
        try {
          await uploadNotaLog(logId, file);
          pesan += " Nota terunggah.";
        } catch (e) {
          toast.error(`Servis tercatat, tapi nota gagal diunggah: ${apiErrorMessage(e, "")}`.trim());
        }
      }
      toast.success(pesan);
      onSelesai(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mencatat servis."));
    } finally {
      setMenyimpan(false);
    }
  };

  if (!aset) return null;
  return (
    <Dialog open={terbuka} onOpenChange={(o) => { if (!o) onTutup(); }}>
      <DialogContent className="max-w-2xl" data-testid="catat-servis-dialog">
        <DialogHeader>
          <DialogTitle>Catat Servis — {aset.kode} {aset.nama}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Tanggal Pelaksanaan</Label>
              <Input type="date" value={form.tanggal_pelaksanaan} onChange={(e) => upd("tanggal_pelaksanaan", e.target.value)} data-testid="servis-tanggal" />
            </div>
            <div>
              <Label>Jenis *</Label>
              <Select value={form.jenis} onValueChange={(v) => upd("jenis", v)}>
                <SelectTrigger data-testid="servis-jenis"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
                <SelectContent>
                  {(meta?.jenis_log || []).map((j) => <SelectItem key={j.code} value={j.code}>{j.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jadwal{form.jenis === "PREVENTIVE" ? " *" : ""}</Label>
              <Select value={form.jadwal_id || TANPA} onValueChange={(v) => upd("jadwal_id", v === TANPA ? "" : v)}>
                <SelectTrigger data-testid="servis-jadwal"><SelectValue placeholder="Tanpa jadwal" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TANPA}>Tanpa jadwal</SelectItem>
                  {jadwalDaftar.map((j) => <SelectItem key={j.id} value={j.id}>{j.nama} ({label(meta, "bentuk_jadwal", j.bentuk_jadwal)})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Dikerjakan oleh *</Label>
            <div className="flex flex-wrap gap-4 text-sm mt-1 mb-1">
              <label className="flex items-center gap-1">
                <input type="radio" name="pelaksana" checked={form.pelaksana_mode === "karyawan"} onChange={() => upd("pelaksana_mode", "karyawan")} data-testid="servis-pelaksana-karyawan" /> Karyawan
              </label>
              <label className="flex items-center gap-1">
                <input type="radio" name="pelaksana" checked={form.pelaksana_mode === "luar"} onChange={() => upd("pelaksana_mode", "luar")} data-testid="servis-pelaksana-luar" /> Teknisi luar
              </label>
            </div>
            {form.pelaksana_mode === "karyawan" ? (
              <PemilihCari value={form.dilaksanakan_oleh || null} onChange={(k) => upd("dilaksanakan_oleh", k?.id || "")}
                           ambilSatu={ambilKaryawan} cari={cariKaryawanAset} label={labelKaryawan} tampilAwal bolehKosong
                           placeholder="Pilih karyawan" placeholderCari="Ketik nama atau kode karyawan…" testId="servis-karyawan" />
            ) : (
              <Input value={form.vendor_teknisi} onChange={(e) => upd("vendor_teknisi", e.target.value)} placeholder="Nama bengkel / teknisi" data-testid="servis-teknisi" />
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Hasil *</Label>
              <Select value={form.hasil} onValueChange={(v) => upd("hasil", v)}>
                <SelectTrigger data-testid="servis-hasil"><SelectValue placeholder="Pilih hasil" /></SelectTrigger>
                <SelectContent>
                  {(meta?.hasil_log || []).map((h) => <SelectItem key={h.code} value={h.code}>{h.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kondisi Setelah</Label>
              <Select value={form.kondisi_setelah || TANPA} onValueChange={(v) => upd("kondisi_setelah", v === TANPA ? "" : v)}>
                <SelectTrigger data-testid="servis-kondisi"><SelectValue placeholder="Pilih kondisi" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TANPA}>Tidak diisi</SelectItem>
                  {(meta?.kondisi || []).map((k) => <SelectItem key={k.code} value={k.code}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Biaya (Rp)</Label>
              <Input type="number" min="0" value={form.biaya_idr} onChange={(e) => upd("biaya_idr", e.target.value)} data-testid="servis-biaya" />
            </div>
            {kendaraan && (
              <div>
                <Label>Km Saat Servis</Label>
                <Input type="number" min="0" value={form.km} onChange={(e) => upd("km", e.target.value)} data-testid="servis-km" />
              </div>
            )}
            <div className={kendaraan ? "" : "sm:col-span-2"}>
              <Label>Nota</Label>
              <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="servis-nota"
                     className="block w-full text-sm text-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-border file:bg-background file:text-sm file:text-foreground hover:file:bg-muted" />
              {file ? <div className="text-xs text-muted-foreground mt-1">{file.name}</div> : null}
            </div>
          </div>

          <button type="button" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => setRinci((r) => !r)} data-testid="servis-rinci-toggle">
            {rinci ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Rincian lain
          </button>
          {rinci && (
            <div className="space-y-3 border-l-2 border-border pl-3" data-testid="servis-rinci">
              <div>
                <Label>Keluhan</Label>
                <Textarea rows={2} value={form.keluhan} onChange={(e) => upd("keluhan", e.target.value)} data-testid="servis-keluhan" />
              </div>
              <div>
                <Label>Catatan</Label>
                <Textarea rows={2} value={form.catatan} onChange={(e) => upd("catatan", e.target.value)} data-testid="servis-catatan" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Pelapor{form.jenis === "CORRECTIVE" ? " *" : ""}</Label>
                  <PemilihCari value={form.dilaporkan_oleh || null} onChange={(k) => upd("dilaporkan_oleh", k?.id || "")}
                               ambilSatu={ambilKaryawan} cari={cariKaryawanAset} label={labelKaryawan} tampilAwal bolehKosong
                               placeholder="Tidak diisi" placeholderCari="Ketik nama atau kode karyawan…" testId="servis-pelapor" />
                </div>
                <div>
                  <Label>Tanggal Lapor</Label>
                  <Input type="date" value={form.tanggal_lapor} onChange={(e) => upd("tanggal_lapor", e.target.value)} data-testid="servis-tanggal-lapor" />
                  <div className="text-xs text-muted-foreground mt-1">Kosong = sama dengan tanggal pelaksanaan.</div>
                </div>
              </div>
            </div>
          )}
          {kendaraan && form.km !== "" && (
            <div className="text-xs text-muted-foreground">Km yang dicatat: {formatKm(form.km)}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onTutup} disabled={menyimpan}>Batal</Button>
          <BusyButton onClick={simpan} busy={menyimpan} busyText="Menyimpan..." data-testid="servis-simpan">Simpan</BusyButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
